import * as React from 'react';
import { css } from '@emotion/react';
import type { ElementTypeLean, MergeFirst } from '@resi-media/resi-ui';
import { Box, mergeDefaultProps } from '@resi-media/resi-ui';

const defaultElement = 'div';

function styleOptionsToCssProperties({
  // baseColor,
  // highlightColor,
  borderRadius,
  circle,
  // direction,
  // duration,
  // enableAnimation = defaultEnableAnimation,

  height,
  width,
}: _Props): React.CSSProperties {
  const style: React.CSSProperties & Record<`--${string}`, string> = {};

  // if (direction === 'rtl') style['--animation-direction'] = 'reverse';
  // if (typeof duration === 'number')
  //   style['--animation-duration'] = `${duration}s`;
  // if (!enableAnimation) style['--pseudo-element-display'] = 'none';

  if (typeof width === 'string' || typeof width === 'number') style.width = width;
  if (typeof height === 'string' || typeof height === 'number') style.height = height;

  if (typeof borderRadius === 'string' || typeof borderRadius === 'number') style.borderRadius = borderRadius;

  if (circle) style.borderRadius = '50%';

  // if (typeof baseColor !== 'undefined') style['--base-color'] = baseColor;
  // if (typeof highlightColor !== 'undefined')
  //   style['--highlight-color'] = highlightColor;

  return style;
}

type _Props<P extends ElementTypeLean = typeof defaultElement> = MergeFirst<
  Box.Props<P>,
  {
    borderRadius?: number | string;
    children?: React.ReactNode;
    circle?: boolean;
    count?: number;
    dataTestId?: string;
    enableAnimation?: boolean;
    height?: number | string;
    inline?: boolean;
    isLoading?: boolean;
    width?: number | string;
  }
> & {
  as?: P;
};

const defaultProps = {
  as: 'div' as ElementTypeLean,
  count: 1,
};

type _InternalProps = MergeFirst<_Props, typeof defaultProps>;

const SkeletonInternal = <T extends ElementTypeLean = typeof defaultElement>(
  props: _Props<T>,
  ref: React.ForwardedRef<HTMLElement>
) => {
  const propsInternal: _InternalProps = mergeDefaultProps(props, defaultProps);
  const { as, borderRadius, children, circle, count, dataTestId, height, isLoading, width, ...rest } = propsInternal;

  const inline = rest.inline ?? false;

  const elements: React.ReactElement[] = [];

  const countCeil = Math.ceil(count);

  for (let i = 0; i < countCeil; i++) {
    let thisStyle = { ...styleOptionsToCssProperties({ borderRadius, circle, height, width, ...rest }) };

    if (countCeil > count && i === countCeil - 1) {
      // count is not an integer and we've reached the last iteration of
      // the loop, so add a "fractional" skeleton.
      //
      // For example, if count is 3.5, we've already added 3 full
      // skeletons, so now we add one more skeleton that is 0.5 times the
      // original width.

      const width = '100%'; // 100% is the default since that's what's in the CSS

      const fractionalPart = count % 1;

      thisStyle = { ...thisStyle, width: `calc(${width} * ${fractionalPart})` };
    }

    const skeletonSpan = (
      <span
        key={i}
        css={(theme) => css`
          @keyframes skeleton-loading {
            0% {
              background-color: ${theme.palette.surface.table};
            }
            100% {
              background-color: ${theme.palette.border};
            }
          }

          background-color: ${theme.palette.divider};

          width: 100%;
          border-radius: 0.25rem;
          animation: skeleton-loading 1s linear infinite alternate;
          display: inline-flex;
          line-height: 1;

          position: relative;
          user-select: none;
          overflow: hidden;
          z-index: 1; /* Necessary for overflow: hidden to work correctly in Safari */

          @media (prefers-reduced-motion) {
            display: none; /* Disable animation */
          }
        `}
        data-testid="skeleton-item"
        style={thisStyle}
      >
        &zwnj;
      </span>
    );

    if (inline) {
      elements.push(skeletonSpan);
    } else {
      elements.push(<React.Fragment key={i}>{skeletonSpan}</React.Fragment>);
    }
  }

  return isLoading ? (
    <Box<typeof as>
      ref={ref}
      aria-busy={rest.enableAnimation}
      aria-live="polite"
      as={as}
      data-testid={dataTestId}
      display="flex"
      flexDirection="column"
      flexWrap="nowrap"
      width={width}
      {...rest}
    >
      {elements}
    </Box>
  ) : (
    <>{children}</>
  );
};

SkeletonInternal.displayName = 'Skeleton';

/* eslint-disable import/export */
export const Skeleton = Object.assign(React.forwardRef(SkeletonInternal), {
  displayName: 'Skeleton',
});

// eslint-disable-next-line no-redeclare
export namespace Skeleton {
  export type Props<T extends ElementTypeLean = typeof defaultElement> = _Props<T>;
}
